import { makeObservable, observable, action, runInAction } from 'mobx';
import { awardBonusCharacters, getGamificationProgress, getUserCharacters } from '../audiblogsApi';
import userStore from './User';
import mixpanel from 'mixpanel-browser';

class GamificationStore {
  // Gamification actions with different bonus amounts
  actions = {
    PODCAST_SETUP: 'podcast_setup',
    SUBSCRIBE_FEED: 'subscribe_feed',
    ADD_ARTICLE: 'add_article',
    CONNECT_PHONE: 'connect_phone',
    INSTALL_EXTENSION: 'install_extension',
    CHOOSE_VOICE: 'choose_voice',
    LEAVE_REVIEW: 'leave_review',
    USE_500K_CHARS: 'use_500k_chars',
    CONVERT_FIRST_EMAIL: 'convert_first_email'
  };

  // Bonus amounts for each action
  bonusAmounts = {
    [this.actions.PODCAST_SETUP]: 250000,    // 250k for podcast setup
    [this.actions.SUBSCRIBE_FEED]: 100000,   // 100k for subscribing to feed
    [this.actions.ADD_ARTICLE]: 100000,      // 100k for adding first article
    [this.actions.CONNECT_PHONE]: 100000,    // 100k for connecting phone
    [this.actions.INSTALL_EXTENSION]: 100000, // 100k for installing extension
    [this.actions.CHOOSE_VOICE]: 100000,     // 100k for choosing voice
    [this.actions.LEAVE_REVIEW]: 200000,     // 200k for leaving review
    [this.actions.USE_500K_CHARS]: 500000,   // 500k for using 500k chars in first 30 days
    [this.actions.CONVERT_FIRST_EMAIL]: 100000 // 100k for converting first email
  };

  // Track completed actions
  completedActions = new Set();
  
  // Track if user has received the completion bonus
  hasReceivedCompletionBonus = false;
  
  // Loading states
  isLoading = false;
  isInitialized = false;
  
  // Backend progress data
  backendProgressData = null;

  constructor() {
          makeObservable(this, {
        completedActions: observable,
        hasReceivedCompletionBonus: observable,
        isLoading: observable,
        isInitialized: observable,
        backendProgressData: observable,
        initialize: action.bound,
        completeAction: action.bound,
        checkCompletionBonus: action.bound,
        reset: action.bound,
      });
  }

  async initialize() {
    if (!userStore.idToken || this.isInitialized) return;
    
    try {
      this.isLoading = true;
      const response = await getGamificationProgress(userStore.idToken);
      
      runInAction(() => {
        // Handle new backend response format
        const data = response.data;
        this.completedActions = new Set(data.completed_actions || []);
        
        // Check if completion bonus has been received by looking for it in completed actions
        this.hasReceivedCompletionBonus = data.completed_actions?.includes('completion_bonus') || false;
        
        // Store backend progress data for calculations
        this.backendProgressData = {
          completion_percentage: data.completion_percentage,
          total_actions: data.total_actions,
          all_actions: data.all_actions
        };
        
        this.isInitialized = true;
      });
    } catch (error) {
      console.error('Error loading gamification progress:', error);
      runInAction(() => {
        this.isInitialized = true;
      });
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  async refreshProgressData() {
    if (!userStore.idToken) return;
    
    try {
      const response = await getGamificationProgress(userStore.idToken);
      
      runInAction(() => {
        // Handle new backend response format
        const data = response.data;
        this.completedActions = new Set(data.completed_actions || []);
        
        // Check if completion bonus has been received by looking for it in completed actions
        this.hasReceivedCompletionBonus = data.completed_actions?.includes('completion_bonus') || false;
        
        // Store backend progress data for calculations, ensuring integers
        this.backendProgressData = {
          completion_percentage: Math.round(data.completion_percentage || 0),
          total_actions: Math.round(data.total_actions || 0),
          all_actions: data.all_actions || []
        };
      });
    } catch (error) {
      console.error('Error refreshing gamification progress:', error);
    }
  }

  // Refresh character data to show updated counts immediately
  async refreshCharacterData() {
    if (!userStore.idToken) return;
    
    try {
      const response = await getUserCharacters(userStore.idToken);
      if (response.data) {
        // Check for 500k character usage bonus
        await this.check500kCharacterUsage(response.data);
        
        // Emit an event that the Settings page can listen to
        window.dispatchEvent(new CustomEvent('characterDataUpdated', {
          detail: response.data
        }));
      }
    } catch (error) {
      console.error('Error refreshing character data:', error);
    }
  }

  async completeAction(action) {
    // Prevent duplicate awards - check both local state and backend
    if (!userStore.idToken || this.completedActions.has(action)) {
      console.log(`Action ${action} already completed, skipping`);
      return false;
    }

    try {
      const bonusAmount = this.bonusAmounts[action];
      // Ensure bonus amount is an integer
      const integerBonusAmount = Math.round(bonusAmount);
      const response = await awardBonusCharacters(userStore.idToken, action, integerBonusAmount);
      
      runInAction(() => {
        this.completedActions.add(action);
        
        // Track in mixpanel
        mixpanel.track('Gamification Action Completed', {
          action,
          bonus_characters: integerBonusAmount,
          total_completed: this.completedActions.size
        });
      });

      // Show notification
      this.showBonusNotification(action);

      // Check if user should get completion bonus
      await this.checkCompletionBonus();
      
      // Refresh backend progress data to update the progress bar immediately
      await this.refreshProgressData();
      
      // Refresh character data to show updated counts immediately
      await this.refreshCharacterData();
      
      return true;
    } catch (error) {
      console.error('Error completing gamification action:', error);
      return false;
    }
  }

  showBonusNotification(action) {
    // Create a simple notification
    const notification = document.createElement('div');
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: linear-gradient(135deg, #4CAF50, #45a049);
      color: white;
      padding: 16px 20px;
      border-radius: 12px;
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
      z-index: 10000;
      font-family: 'Cabin', sans-serif;
      font-weight: 600;
      animation: slideIn 0.3s ease;
      max-width: 300px;
    `;
    
    const actionNames = {
      [this.actions.PODCAST_SETUP]: 'Podcast Setup',
      [this.actions.SUBSCRIBE_FEED]: 'Feed Subscription',
      [this.actions.ADD_ARTICLE]: 'First Article',
      [this.actions.CONNECT_PHONE]: 'Phone Setup',
      [this.actions.INSTALL_EXTENSION]: 'Extension Install',
      [this.actions.CHOOSE_VOICE]: 'Voice Selection',
      [this.actions.LEAVE_REVIEW]: 'Review',
      [this.actions.USE_500K_CHARS]: '500k Characters Used',
      [this.actions.CONVERT_FIRST_EMAIL]: 'First Email Converted',
      'completion_bonus': 'Completion Bonus'
    };

    const bonusAmount = this.bonusAmounts[action] || 250000; // Default to 250k for completion bonus
    // Ensure we don't create float values from division
    const bonusText = bonusAmount >= 1000000 ? `+${Math.round(bonusAmount / 1000000)}M` : `+${Math.round(bonusAmount / 1000)}k`;
    
    notification.innerHTML = `
      <div style="display: flex; align-items: center; gap: 12px;">
        <span style="font-size: 20px;">🎁</span>
        <div>
          <div style="font-size: 16px; margin-bottom: 4px;">${bonusText} Characters!</div>
          <div style="font-size: 14px; opacity: 0.9;">${actionNames[action] || action}</div>
        </div>
      </div>
    `;

    // Add CSS animation
    const style = document.createElement('style');
    style.textContent = `
      @keyframes slideIn {
        from { transform: translateX(100%); opacity: 0; }
        to { transform: translateX(0); opacity: 1; }
      }
    `;
    document.head.appendChild(style);

    document.body.appendChild(notification);

    // Remove after 4 seconds
    setTimeout(() => {
      notification.style.animation = 'slideOut 0.3s ease';
      setTimeout(() => {
        document.body.removeChild(notification);
        document.head.removeChild(style);
      }, 300);
    }, 4000);

    // Add slideOut animation
    const slideOutStyle = document.createElement('style');
    slideOutStyle.textContent = `
      @keyframes slideOut {
        from { transform: translateX(0); opacity: 1; }
        to { transform: translateX(100%); opacity: 0; }
      }
    `;
    document.head.appendChild(slideOutStyle);
  }

  async checkCompletionBonus() {
    console.log('Checking completion bonus...');
    console.log('Already received:', this.hasReceivedCompletionBonus);
    console.log('Backend progress data:', this.backendProgressData);
    console.log('Completed actions:', Array.from(this.completedActions));
    
    if (this.hasReceivedCompletionBonus) {
      console.log('Completion bonus already received, skipping');
      return;
    }
    
    // Use backend data if available, otherwise check locally
    let allActionsCompleted = false;
    if (this.backendProgressData?.completion_percentage === 100) {
      allActionsCompleted = true;
      console.log('All actions completed according to backend (100%)');
    } else {
      allActionsCompleted = Object.values(this.actions).every(action => 
        this.completedActions.has(action)
      );
      console.log('All actions completed locally:', allActionsCompleted);
    }
    
    if (allActionsCompleted) {
      try {
        const completionBonusAmount = 250000;
        // Ensure completion bonus amount is an integer
        const integerCompletionBonusAmount = Math.round(completionBonusAmount);
        await awardBonusCharacters(userStore.idToken, 'completion_bonus', integerCompletionBonusAmount);
        
        runInAction(() => {
          this.hasReceivedCompletionBonus = true;
        });
        
        // Show completion bonus notification
        this.showBonusNotification('completion_bonus');
        
        // Track completion bonus
        mixpanel.track('Gamification Completion Bonus Awarded', {
          bonus_characters: integerCompletionBonusAmount,
          total_bonus_characters: 1700000 // 250k + 100k + 100k + 100k + 200k + 500k + 100k + 250k completion
        });

        // Refresh progress data to update the UI
        await this.refreshProgressData();
        
        // Refresh character data to show updated counts immediately
        await this.refreshCharacterData();
      } catch (error) {
        console.error('Error awarding completion bonus:', error);
      }
    }
  }

  isActionCompleted(action) {
    return this.completedActions.has(action);
  }

  getProgressPercentage() {
    // Use backend data if available, otherwise calculate locally
    if (this.backendProgressData) {
      console.log('Using backend progress data:', {
        completion_percentage: this.backendProgressData.completion_percentage,
        total_actions: this.backendProgressData.total_actions,
        all_actions: this.backendProgressData.all_actions
      });
      return this.backendProgressData.completion_percentage || 0;
    }
    
    const totalActions = Object.keys(this.actions).length;
    const completedCount = this.completedActions.size;
    console.log('Using local progress calculation:', {
      totalActions,
      completedCount,
      completedActions: Array.from(this.completedActions),
      allActions: Object.values(this.actions)
    });
    // Ensure we return an integer, not a float
    return Math.round((completedCount / totalActions) * 100);
  }

  getRemainingActions() {
    return Object.values(this.actions).filter(action => 
      !this.completedActions.has(action)
    );
  }

  getTotalActionsCount() {
    // Use backend data if available, otherwise use local count
    if (this.backendProgressData?.total_actions) {
      console.log('Backend total actions:', this.backendProgressData.total_actions);
      return this.backendProgressData.total_actions;
    }
    const localCount = Object.keys(this.actions).length;
    console.log('Local total actions:', localCount);
    return localCount;
  }

  getCompletedActionsCount() {
    return this.completedActions.size;
  }

  reset() {
    this.completedActions.clear();
    this.hasReceivedCompletionBonus = false;
    this.isInitialized = false;
    this.isLoading = false;
    this.backendProgressData = null;
  }

  // Check if user should get 500k character usage bonus
  async check500kCharacterUsage(characterData) {
    console.log('Checking 500k character usage bonus...');
    console.log('Character data:', characterData);
    console.log('Action already completed:', this.completedActions.has(this.actions.USE_500K_CHARS));
    
    if (!characterData || this.completedActions.has(this.actions.USE_500K_CHARS)) {
      console.log('Skipping 500k check - no data or already completed');
      return;
    }

    // Ensure we're working with integers
    const totalCharacters = Math.round(characterData.total_characters || 0);
    
    // Use signup_date from backend response
    let userCreatedAt = userStore.user?.signup_date;
    
    console.log('Total characters used:', totalCharacters);
    console.log('User signup date:', userCreatedAt);
    
    if (!userCreatedAt) {
      console.log('No user creation date found from any source');
      return;
    }

    // Check if user is within first 30 days
    const createdAt = new Date(userCreatedAt);
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    
    const isWithinFirstThirtyDays = createdAt > thirtyDaysAgo;
    const hasUsed500k = totalCharacters >= 500000;

    console.log('User creation date:', createdAt);
    console.log('Thirty days ago:', thirtyDaysAgo);
    console.log('Is within first thirty days:', isWithinFirstThirtyDays);
    console.log('Has used 500k:', hasUsed500k);

    if (isWithinFirstThirtyDays && hasUsed500k) {
      console.log('Awarding 500k character usage bonus!');
      await this.completeAction(this.actions.USE_500K_CHARS);
    } else {
      console.log('Not eligible for 500k bonus:', { isWithinFirstThirtyDays, hasUsed500k });
    }
  }

  // Check if user is still eligible for 500k character usage bonus (for UI display)
  isEligibleFor500kBonus() {
    // If already completed, not eligible
    if (this.completedActions.has(this.actions.USE_500K_CHARS)) {
      return false;
    }

    // Check if user is within first 30 days
    const userCreatedAt = userStore.user?.signup_date;
    if (!userCreatedAt) {
      return false;
    }

    const createdAt = new Date(userCreatedAt);
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    
    return createdAt > thirtyDaysAgo;
  }

  // Development helper - manually trigger bonuses for testing
  async triggerAction(action) {
    if (process.env.NODE_ENV === 'development') {
      return await this.completeAction(action);
    }
    return false;
  }
}

const gamificationStore = new GamificationStore();

export default gamificationStore; 